import {useEffect, useMemo, useState} from 'react'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
} from 'date-fns'
import {useBirthdays} from '../../hooks/useBirthdays'
import {useGetUser} from '../../store/userStore'
import {parseId} from '../../helpers'
import {useQuery} from '@apollo/client'
import {GET_APPOINTMENTS_BY_RANGE} from '../../gql/queries/appointmentsQuery'
import {GET_KANBAN_TASKS} from '../../gql/queries/kanban'
import {GET_POLICY_DOCUMENTS_DUE_BY_RANGE} from '../../gql/queries/policiesApiQueries'
import {GET_TICKETS} from '../../gql/queries/ticketsQuery'
import {BirthdayIcon} from './BirthdayIcon'
import {BirthdayListItem} from './BirthdayListItem'
import {BirthdayModal} from './BirthdayModal'
import {BirthdayMember} from '../../types/birthdays'
import {AppointmentIcon} from './AppointmentIcon'
import {AppointmentListItem} from './AppointmentListItem'
import {AppointmentModal} from './AppointmentModal'
import {ModalPreview} from '../contacts/components/ModalPreview'
import {Link} from 'react-router-dom'
import './CalendarStyles.css'

type ViewMode = 'month' | 'week'
const CALENDAR_SWITCHES_STORAGE_KEY = 'calendar-visibility-switches'

export const CalendarPage = () => {
  const [currentDate, setCurrentDate] = useState(new Date())
  const [viewMode, setViewMode] = useState<ViewMode>('month')
  const [showBirthdays, setShowBirthdays] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CALENDAR_SWITCHES_STORAGE_KEY)
      if (!raw) return true
      const parsed = JSON.parse(raw)
      return parsed.showBirthdays ?? true
    } catch {
      return true
    }
  })
  const [showLeadAppointments, setShowLeadAppointments] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CALENDAR_SWITCHES_STORAGE_KEY)
      if (!raw) return true
      const parsed = JSON.parse(raw)
      return parsed.showLeadAppointments ?? true
    } catch {
      return true
    }
  })
  const [showPolicyDocumentsDueDate, setShowPolicyDocumentsDueDate] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CALENDAR_SWITCHES_STORAGE_KEY)
      if (!raw) return true
      const parsed = JSON.parse(raw)
      return parsed.showPolicyDocumentsDueDate ?? true
    } catch {
      return true
    }
  })
  const [showTicketsDueDate, setShowTicketsDueDate] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CALENDAR_SWITCHES_STORAGE_KEY)
      if (!raw) return true
      const parsed = JSON.parse(raw)
      return parsed.showTicketsDueDate ?? true
    } catch {
      return true
    }
  })
  const [showTasksDueDate, setShowTasksDueDate] = useState(() => {
    try {
      const raw = window.localStorage.getItem(CALENDAR_SWITCHES_STORAGE_KEY)
      if (!raw) return true
      const parsed = JSON.parse(raw)
      return parsed.showTasksDueDate ?? true
    } catch {
      return true
    }
  })
  const [selectedDayBirthdays, setSelectedDayBirthdays] = useState<{
    birthdays: BirthdayMember[]
    date: Date
  } | null>(null)
  const [selectedAppointment, setSelectedAppointment] = useState<AppointmentEdge['node'] | null>(
    null
  )
  const [previewContactId, setPreviewContactId] = useState<string | null>(null)
  const [loadingDay, setLoadingDay] = useState<number | null>(null)
  const [loadingDayKey, setLoadingDayKey] = useState<string | null>(null)
  const [expandedDays, setExpandedDays] = useState<Record<string, boolean>>({})
  const [dayOverrides, setDayOverrides] = useState<Record<string, BirthdayMember[]>>({})
  const currentUser = useGetUser((s) => s.user?.user)
  const currentUserId = currentUser?.id ? Number(parseId(currentUser.id)) : 0

  useEffect(() => {
    try {
      window.localStorage.setItem(
        CALENDAR_SWITCHES_STORAGE_KEY,
        JSON.stringify({
          showBirthdays,
          showLeadAppointments,
          showPolicyDocumentsDueDate,
          showTicketsDueDate,
          showTasksDueDate,
        })
      )
    } catch {
      // ignore localStorage write errors
    }
  }, [
    showBirthdays,
    showLeadAppointments,
    showPolicyDocumentsDueDate,
    showTicketsDueDate,
    showTasksDueDate,
  ])

  // Obtener el mes del currentDate para pasarlo al hook
  const currentMonth = currentDate.getMonth() + 1 // getMonth() retorna 0-11, necesitamos 1-12
  const currentYear = currentDate.getFullYear()

  // Obtener cumpleaños de contactos del mes actual
  const {birthdaysByDate, loading, isPartial, loadDayBirthdays} = useBirthdays(currentMonth, {
    skip: !showBirthdays,
  })
  const visibleRange = useMemo(() => {
    if (viewMode === 'month') {
      const monthStart = startOfMonth(currentDate)
      const monthEnd = endOfMonth(monthStart)
      return {
        start: startOfWeek(monthStart),
        end: endOfWeek(monthEnd),
      }
    }

    const weekStart = startOfWeek(currentDate)
    return {
      start: weekStart,
      end: endOfWeek(weekStart),
    }
  }, [currentDate, viewMode])

  const rangeStart = useMemo(
    () =>
      new Date(
        visibleRange.start.getFullYear(),
        visibleRange.start.getMonth(),
        visibleRange.start.getDate(),
        0,
        0,
        0,
        0
      ),
    [visibleRange]
  )
  const rangeEnd = useMemo(
    () =>
      new Date(
        visibleRange.end.getFullYear(),
        visibleRange.end.getMonth(),
        visibleRange.end.getDate(),
        23,
        59,
        59,
        999
      ),
    [visibleRange]
  )
  const {data: appointmentsData, loading: appointmentsLoading} = useQuery<{
    allAppointments: {
      edges: {
        node: {
          id: string
          tittle: string
          date: string
          meetingLocation?: string | null
          typeAppointment?: string | null
          comment?: string | null
          assignedTo?: {id: string; firstName?: string | null; lastName?: string | null} | null
          creditTo?: {id: string; firstName?: string | null; lastName?: string | null} | null
          lead?: {
            id: string
            name: string
            stagePipeline?: {name: string; pipeline?: {name: string}}
          }
        }
      }[]
    }
  }>(GET_APPOINTMENTS_BY_RANGE, {
    variables: {
      date_Gte: rangeStart.toISOString(),
      date_Lte: rangeEnd.toISOString(),
      assignedTo_Id: currentUserId || undefined,
    },
    skip: !currentUserId || !showLeadAppointments,
    fetchPolicy: 'cache-and-network',
  })

  type PolicyDocumentsDueEdge = {
    node: {
      id: string
      type?: string | null
      policyName?: string | null
      documentsDueDate?: string | null
      holderMember?: {
        id?: string
        firstName?: string | null
        lastName?: string | null
      } | null
    }
  }

  const {data: documentsDueData, loading: documentsDueLoading} = useQuery<{
    allPolicies: {
      edges: PolicyDocumentsDueEdge[]
    }
  }>(GET_POLICY_DOCUMENTS_DUE_BY_RANGE, {
    variables: {
      first: 100,
      documentsDueDateAfter: format(rangeStart, 'yyyy-MM-dd'),
      documentsDueDateBefore: format(rangeEnd, 'yyyy-MM-dd'),
      documentStatus: ['PENDING'],
    },
    skip: !showPolicyDocumentsDueDate,
    fetchPolicy: 'cache-and-network',
  })

  type TicketCalendarItem = {
    id: string
    title?: string | null
    dueAt?: string | null
    priority?: string | null
    status?: string | null
  }

  const {data: ticketsDueData, loading: ticketsDueLoading} = useQuery<{
    tickets: {
      items: TicketCalendarItem[]
    }
  }>(GET_TICKETS, {
    variables: {
      first: 100,
      offset: 0,
      dueAfter: rangeStart.toISOString(),
      dueBefore: rangeEnd.toISOString(),
      includeArchived: false,
      includeSolvedClosed: false,
    },
    skip: !showTicketsDueDate,
    fetchPolicy: 'cache-and-network',
  })

  type KanbanTaskCalendarItem = {
    id: string
    title: string
    dueDate?: string | null
    workflow: {
      id: string
      name: string
    }
  }

  const {data: tasksDueData, loading: tasksDueLoading} = useQuery<{
    kanbanTasks: KanbanTaskCalendarItem[]
  }>(GET_KANBAN_TASKS, {
    variables: {
      dueDateAfter: rangeStart.toISOString(),
      dueDateBefore: rangeEnd.toISOString(),
      isCompleted: false,
    },
    skip: !showTasksDueDate,
    fetchPolicy: 'cache-and-network',
  })

  type AppointmentEdge = {
    node: {
      id: string
      tittle: string
      date: string
      meetingLocation?: string | null
      typeAppointment?: string | null
      comment?: string | null
      assignedTo?: {id: string; firstName?: string | null; lastName?: string | null} | null
      creditTo?: {id: string; firstName?: string | null; lastName?: string | null} | null
      lead?: {
        id: string
        name: string
        stagePipeline?: {name: string; pipeline?: {name: string}}
      }
    }
  }

  const appointmentsByDate = useMemo<Record<string, AppointmentEdge[]>>(() => {
    const edges = appointmentsData?.allAppointments?.edges ?? []
    return edges.reduce((acc, edge) => {
      const parsed = new Date(edge.node.date)
      if (Number.isNaN(parsed.getTime())) return acc
      const dayKey = format(parsed, 'MM-dd')
      if (!acc[dayKey]) acc[dayKey] = []
      acc[dayKey].push(edge)
      return acc
    }, {} as Record<string, AppointmentEdge[]>)
  }, [appointmentsData])

  const documentsDueByDate = useMemo<Record<string, PolicyDocumentsDueEdge[]>>(() => {
    const edges = documentsDueData?.allPolicies?.edges ?? []
    return edges.reduce((acc, edge) => {
      if (!edge.node.documentsDueDate) return acc
      const parsed = new Date(`${edge.node.documentsDueDate}T00:00:00`)
      if (Number.isNaN(parsed.getTime())) return acc
      const dayKey = format(parsed, 'MM-dd')
      if (!acc[dayKey]) acc[dayKey] = []
      acc[dayKey].push(edge)
      return acc
    }, {} as Record<string, PolicyDocumentsDueEdge[]>)
  }, [documentsDueData])

  const ticketsDueByDate = useMemo<Record<string, TicketCalendarItem[]>>(() => {
    const items = ticketsDueData?.tickets?.items ?? []
    return items.reduce((acc, item) => {
      if (!item.dueAt) return acc
      const parsed = new Date(item.dueAt)
      if (Number.isNaN(parsed.getTime())) return acc
      const dayKey = format(parsed, 'MM-dd')
      if (!acc[dayKey]) acc[dayKey] = []
      acc[dayKey].push(item)
      return acc
    }, {} as Record<string, TicketCalendarItem[]>)
  }, [ticketsDueData])

  const tasksDueByDate = useMemo<Record<string, KanbanTaskCalendarItem[]>>(() => {
    const items = tasksDueData?.kanbanTasks ?? []
    return items.reduce((acc, item) => {
      if (!item.dueDate) return acc
      const parsed = new Date(item.dueDate)
      if (Number.isNaN(parsed.getTime())) return acc
      const dayKey = format(parsed, 'MM-dd')
      if (!acc[dayKey]) acc[dayKey] = []
      acc[dayKey].push(item)
      return acc
    }, {} as Record<string, KanbanTaskCalendarItem[]>)
  }, [tasksDueData])

  const openPreview = (contactId: string) => setPreviewContactId(contactId)
  const closePreview = () => setPreviewContactId(null)

  const getDayKey = (date: Date) => format(date, 'MM-dd')

  const getBirthdaysForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return dayOverrides[monthDay] || birthdaysByDate[monthDay] || []
  }

  const getAppointmentsForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return appointmentsByDate[monthDay] || []
  }

  const getDocumentsDueForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return documentsDueByDate[monthDay] || []
  }

  const getTicketsDueForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return ticketsDueByDate[monthDay] || []
  }

  const getTasksDueForDate = (date: Date) => {
    const monthDay = getDayKey(date)
    return tasksDueByDate[monthDay] || []
  }

  const getPolicyDisplayName = (policy: PolicyDocumentsDueEdge['node']) => {
    const holderName = [policy.holderMember?.firstName, policy.holderMember?.lastName]
      .filter(Boolean)
      .join(' ')
      .trim()
    return holderName || policy.policyName || 'Policy'
  }

  const handleShowMore = async (date: Date, dayBirthdays: BirthdayMember[]) => {
    const dayNumber = date.getDate()
    if (isPartial) {
      try {
        setLoadingDay(dayNumber)
        const fullDay = await loadDayBirthdays(dayNumber)
        setSelectedDayBirthdays({birthdays: fullDay, date})
      } catch (e) {
        // Silently ignore; could add toast if desired
        setSelectedDayBirthdays({birthdays: dayBirthdays, date})
      } finally {
        setLoadingDay(null)
      }
      return
    }
    setSelectedDayBirthdays({birthdays: dayBirthdays, date})
  }

  const handleWeekShowMore = async (date: Date, dayBirthdays: BirthdayMember[]) => {
    const dayKey = getDayKey(date)
    if (expandedDays[dayKey]) return

    if (isPartial && !dayOverrides[dayKey]) {
      try {
        setLoadingDayKey(dayKey)
        const fullDay = await loadDayBirthdays(date.getDate())
        setDayOverrides((prev) => ({...prev, [dayKey]: fullDay}))
        setExpandedDays((prev) => ({...prev, [dayKey]: true}))
      } catch (e) {
        setExpandedDays((prev) => ({...prev, [dayKey]: true}))
      } finally {
        setLoadingDayKey(null)
      }
      return
    }

    setExpandedDays((prev) => ({...prev, [dayKey]: true}))
  }

  const renderMonthView = () => {
    const monthStart = startOfMonth(currentDate)
    const monthEnd = endOfMonth(monthStart)
    const startDate = startOfWeek(monthStart)
    const endDate = endOfWeek(monthEnd)

    const rows: JSX.Element[] = []
    let days: JSX.Element[] = []
    let day = startDate

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const cloneDay = day
        const dayBirthdays = getBirthdaysForDate(cloneDay)
        const dayAppointments = getAppointmentsForDate(cloneDay)
        const dayDocumentsDue = getDocumentsDueForDate(cloneDay)
        const dayTicketsDue = getTicketsDueForDate(cloneDay)
        const dayTasksDue = getTasksDueForDate(cloneDay)
        const visibleLimit = 5
        const visibleBirthdays = dayBirthdays.slice(0, visibleLimit)
        const remainingCount = dayBirthdays.length - visibleLimit
        const visibleAppointments = dayAppointments.slice(0, 3)
        const visibleDocumentsDue = dayDocumentsDue.slice(0, 2)
        const visibleTicketsDue = dayTicketsDue.slice(0, 2)
        const visibleTasksDue = dayTasksDue.slice(0, 2)

        days.push(
          <div
            key={day.toString()}
            className={`calendar-day ${loading ? 'loading' : ''} ${
              !isSameMonth(day, monthStart) ? 'disabled' : ''
            } ${isSameDay(day, new Date()) ? 'today' : ''}`}
          >
            <span className='day-number'>{format(day, 'd')}</span>
            {loading ? (
              <div className='day-loading'>
                <div className='skeleton-line long' />
                <div className='skeleton-line short' />
              </div>
            ) : (
              <>
                {showBirthdays && dayBirthdays.length > 0 && (
                  <div className='birthday-icons-container'>
                    {visibleBirthdays.map((birthday) => (
                      <BirthdayIcon
                        key={birthday.id}
                        birthday={birthday}
                        onOpenPreview={openPreview}
                      />
                    ))}
                    {(remainingCount > 0 || (isPartial && dayBirthdays.length > 0)) && (
                      <button
                        className='birthday-more-button'
                        onClick={() => handleShowMore(cloneDay, dayBirthdays)}
                        disabled={loadingDay === cloneDay.getDate()}
                      >
                        {loadingDay === cloneDay.getDate()
                          ? 'Loading...'
                          : isPartial
                          ? 'Show more'
                          : `+${remainingCount} more`}
                      </button>
                    )}
                  </div>
                )}
                {showLeadAppointments && dayAppointments.length > 0 && !appointmentsLoading && (
                  <div className='appointment-icons-container'>
                    {visibleAppointments.map(({node}) => (
                      <AppointmentIcon
                        key={node.id}
                        title={node.tittle}
                        date={node.date}
                        onClick={() => setSelectedAppointment(node)}
                      />
                    ))}
                    {dayAppointments.length > visibleAppointments.length && (
                      <div className='calendar-appointments-title'>
                        +{dayAppointments.length - visibleAppointments.length} more appointments
                      </div>
                    )}
                  </div>
                )}
                {showPolicyDocumentsDueDate && dayDocumentsDue.length > 0 && !documentsDueLoading && (
                  <div className='documents-due-icons-container'>
                    {visibleDocumentsDue.map(({node}) => (
                      <Link
                        key={node.id}
                        className='documents-due-link'
                        to={node.type === 'QUOTE' ? `/one-quote/${parseId(node.id)}` : `/one-policy/${parseId(node.id)}`}
                      >
                        <i className='bi bi-file-earmark-text'></i>
                        <span>{getPolicyDisplayName(node)}</span>
                      </Link>
                    ))}
                    {dayDocumentsDue.length > visibleDocumentsDue.length && (
                      <div className='calendar-documents-due-title'>
                        +{dayDocumentsDue.length - visibleDocumentsDue.length} more docs due
                      </div>
                    )}
                  </div>
                )}
                {showTicketsDueDate && dayTicketsDue.length > 0 && !ticketsDueLoading && (
                  <div className='tickets-due-icons-container'>
                    {visibleTicketsDue.map((ticket) => (
                      <Link key={ticket.id} className='tickets-due-link' to={`/tickets/${parseId(ticket.id)}`}>
                        <i className='bi bi-ticket-detailed'></i>
                        <span>{ticket.title || `Ticket #${parseId(ticket.id)}`}</span>
                      </Link>
                    ))}
                    {dayTicketsDue.length > visibleTicketsDue.length && (
                      <div className='calendar-tickets-due-title'>
                        +{dayTicketsDue.length - visibleTicketsDue.length} more tickets due
                      </div>
                    )}
                  </div>
                )}
                {showTasksDueDate && dayTasksDue.length > 0 && !tasksDueLoading && (
                  <div className='tasks-due-icons-container'>
                    {visibleTasksDue.map((task) => (
                      <Link
                        key={task.id}
                        className='tasks-due-link'
                        to={`/kanban?task=${parseId(task.id)}`}
                      >
                        <i className='bi bi-kanban'></i>
                        <span>{task.title}</span>
                      </Link>
                    ))}
                    {dayTasksDue.length > visibleTasksDue.length && (
                      <div className='calendar-tasks-due-title'>
                        +{dayTasksDue.length - visibleTasksDue.length} more tasks due
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )
        day = addDays(day, 1)
      }
      rows.push(
        <div className='calendar-row' key={day.toString()}>
          {days}
        </div>
      )
      days = []
    }

    return <div className='calendar-body'>{rows}</div>
  }

  const renderWeekView = () => {
    const weekStart = startOfWeek(currentDate)
    const days: JSX.Element[] = []

    for (let i = 0; i < 7; i++) {
      const day = addDays(weekStart, i)
      const dayBirthdays = getBirthdaysForDate(day)
      const dayAppointments = getAppointmentsForDate(day)
      const dayDocumentsDue = getDocumentsDueForDate(day)
      const dayTicketsDue = getTicketsDueForDate(day)
      const dayTasksDue = getTasksDueForDate(day)
      const dayKey = getDayKey(day)
      const visibleLimit = 5
      const isExpanded = !isPartial || !!expandedDays[dayKey]
      const needsShowMore = isPartial && !isExpanded && dayBirthdays.length > 0
      const visibleList =
        !isPartial || isExpanded ? dayBirthdays : dayBirthdays.slice(0, visibleLimit)
      const hasVisibleBirthdays = showBirthdays && dayBirthdays.length > 0
      const hasVisibleAppointments = showLeadAppointments && dayAppointments.length > 0
      const hasVisibleDocumentsDue = showPolicyDocumentsDueDate && dayDocumentsDue.length > 0
      const hasVisibleTicketsDue = showTicketsDueDate && dayTicketsDue.length > 0
      const hasVisibleTasksDue = showTasksDueDate && dayTasksDue.length > 0
      const hasVisibleEvents =
        hasVisibleBirthdays ||
        hasVisibleAppointments ||
        hasVisibleDocumentsDue ||
        hasVisibleTicketsDue ||
        hasVisibleTasksDue

      days.push(
        <div key={i} className='week-list-item'>
          <div className='week-day-header'>
            <span className='week-day-name'>{format(day, 'EEEE')}</span>
            <span className={`week-day-number ${isToday(day) ? 'today' : ''}`}>
              {format(day, 'MMM d, yyyy')}
            </span>
          </div>
          <div className={`week-day-content ${isExpanded ? 'expanded' : ''}`}>
            {loading ? (
              <div className='day-loading'>
                <div className='skeleton-line long' />
                <div className='skeleton-line short' />
              </div>
            ) : hasVisibleBirthdays ? (
              <>
                <div className={`birthdays-list ${isExpanded ? 'week-day-scroll' : ''}`}>
                  {visibleList.map((birthday) => (
                    <BirthdayListItem
                      key={birthday.id}
                      birthday={birthday}
                      onOpenPreview={openPreview}
                    />
                  ))}
                </div>
                {needsShowMore && (
                  <button
                    className='birthday-more-button mt-2'
                    onClick={() => handleWeekShowMore(day, dayBirthdays)}
                    disabled={loadingDayKey === dayKey}
                  >
                    {loadingDayKey === dayKey
                      ? 'Loading...'
                      : isPartial
                      ? 'Show more'
                      : `+${dayBirthdays.length - visibleList.length} more`}
                  </button>
                )}
              </>
            ) : !hasVisibleEvents ? (
              <div className='no-events'>No events</div>
            ) : null}
            {showLeadAppointments && dayAppointments.length > 0 && !appointmentsLoading && (
              <div className='calendar-appointments-section'>
                <div className='calendar-appointments-title'>Appointments</div>
                <div className='birthdays-list'>
                  {dayAppointments.map(({node}) => (
                    <AppointmentListItem
                      key={node.id}
                      title={node.tittle}
                      date={node.date}
                      meetingLocation={node.meetingLocation}
                      typeAppointment={node.typeAppointment}
                      leadName={node.lead?.name}
                      onClick={() => setSelectedAppointment(node)}
                    />
                  ))}
                </div>
              </div>
            )}
            {showPolicyDocumentsDueDate && dayDocumentsDue.length > 0 && !documentsDueLoading && (
              <div className='calendar-documents-due-section'>
                <div className='calendar-documents-due-title'>Policy Documents Due Date</div>
                <div className='birthdays-list'>
                  {dayDocumentsDue.map(({node}) => (
                    <Link
                      key={node.id}
                      className='documents-due-list-item'
                      to={node.type === 'QUOTE' ? `/one-quote/${parseId(node.id)}` : `/one-policy/${parseId(node.id)}`}
                    >
                      <div className='documents-due-list-icon'>
                        <i className='bi bi-file-earmark-text'></i>
                      </div>
                      <div className='documents-due-list-content'>
                        <div className='documents-due-list-title'>{getPolicyDisplayName(node)}</div>
                        <div className='documents-due-list-meta'>
                          Due: {node.documentsDueDate ? format(new Date(`${node.documentsDueDate}T00:00:00`), 'MMM dd, yyyy') : 'N/A'}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {showTicketsDueDate && dayTicketsDue.length > 0 && !ticketsDueLoading && (
              <div className='calendar-tickets-due-section'>
                <div className='calendar-tickets-due-title'>Tickets Due Date</div>
                <div className='birthdays-list'>
                  {dayTicketsDue.map((ticket) => (
                    <Link key={ticket.id} className='tickets-due-list-item' to={`/tickets/${parseId(ticket.id)}`}>
                      <div className='tickets-due-list-icon'>
                        <i className='bi bi-ticket-detailed'></i>
                      </div>
                      <div className='tickets-due-list-content'>
                        <div className='tickets-due-list-title'>{ticket.title || `Ticket #${parseId(ticket.id)}`}</div>
                        <div className='tickets-due-list-meta'>
                          Due: {ticket.dueAt ? format(new Date(ticket.dueAt), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
            {showTasksDueDate && dayTasksDue.length > 0 && !tasksDueLoading && (
              <div className='calendar-tasks-due-section'>
                <div className='calendar-tasks-due-title'>Tasks Due Date</div>
                <div className='birthdays-list'>
                  {dayTasksDue.map((task) => (
                    <Link
                      key={task.id}
                      className='tasks-due-list-item'
                      to={`/kanban?task=${parseId(task.id)}`}
                    >
                      <div className='tasks-due-list-icon'>
                        <i className='bi bi-kanban'></i>
                      </div>
                      <div className='tasks-due-list-content'>
                        <div className='tasks-due-list-title'>{task.title}</div>
                        <div className='tasks-due-list-meta'>
                          Board: {task.workflow?.name || 'Board'}
                        </div>
                        <div className='tasks-due-list-meta'>
                          Due: {task.dueDate ? format(new Date(task.dueDate), 'MMM dd, yyyy hh:mm a') : 'N/A'}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )
    }

    return <div className='week-list-view'>{days}</div>
  }

  const nextPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(addMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, 7))
    }
  }

  const prevPeriod = () => {
    if (viewMode === 'month') {
      setCurrentDate(subMonths(currentDate, 1))
    } else {
      setCurrentDate(addDays(currentDate, -7))
    }
  }

  const goToToday = () => {
    setCurrentDate(new Date())
  }

  const renderHeader = () => {
    return (
      <div className='calendar-header'>
        <div className='header-left'>
          <h2>{format(currentDate, 'MMMM yyyy')}</h2>
        </div>
        <div className='header-right'>
          <button className='btn btn-sm btn-light me-2' onClick={goToToday}>
            Today
          </button>
          <button className='btn btn-sm btn-icon btn-light me-2' onClick={prevPeriod}>
            <i className='bi bi-chevron-left'></i>
          </button>
          <button className='btn btn-sm btn-icon btn-light me-3' onClick={nextPeriod}>
            <i className='bi bi-chevron-right'></i>
          </button>
          <div className='btn-group'>
            <button
              className={`btn btn-sm ${viewMode === 'month' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setViewMode('month')}
            >
              Month
            </button>
            <button
              className={`btn btn-sm ${viewMode === 'week' ? 'btn-primary' : 'btn-light'}`}
              onClick={() => setViewMode('week')}
            >
              Week List
            </button>
          </div>
        </div>
      </div>
    )
  }

  const renderToggles = () => (
    <div className='calendar-switches'>
      <FormSwitch
        label='Birthdays'
        checked={showBirthdays}
        onChange={() => setShowBirthdays((prev) => !prev)}
        tone='birthday'
        icon='bi-gift'
      />
      <FormSwitch
        label='Lead Appointments'
        checked={showLeadAppointments}
        onChange={() => setShowLeadAppointments((prev) => !prev)}
        tone='appointment'
        icon='bi-calendar-event'
      />
      <FormSwitch
        label='Policy Documents Due Date'
        checked={showPolicyDocumentsDueDate}
        onChange={() => setShowPolicyDocumentsDueDate((prev) => !prev)}
        tone='documents'
        icon='bi-file-earmark-text'
      />
      <FormSwitch
        label='Tickets Due Date'
        checked={showTicketsDueDate}
        onChange={() => setShowTicketsDueDate((prev) => !prev)}
        tone='tickets'
        icon='bi-ticket-detailed'
      />
      <FormSwitch
        label='Tasks Due Date'
        checked={showTasksDueDate}
        onChange={() => setShowTasksDueDate((prev) => !prev)}
        tone='tasks'
        icon='bi-kanban'
      />
    </div>
  )

  const FormSwitch = ({
    label,
    checked,
    onChange,
    tone,
    icon,
  }: {
    label: string
    checked: boolean
    onChange: () => void
    tone: 'birthday' | 'appointment' | 'documents' | 'tickets' | 'tasks'
    icon: string
  }) => (
    <label className='calendar-switch'>
      <input type='checkbox' checked={checked} onChange={onChange} />
      <span className={`calendar-switch-badge calendar-switch-badge--${tone}`}>
        <i className={`bi ${icon}`}></i>
      </span>
      <span>{label}</span>
    </label>
  )

  const renderDaysOfWeek = () => {
    const days: JSX.Element[] = []
    const startDate = startOfWeek(currentDate)

    for (let i = 0; i < 7; i++) {
      days.push(
        <div className='calendar-day-name' key={i}>
          {format(addDays(startDate, i), 'EEE')}
        </div>
      )
    }

    return <div className='calendar-days-of-week'>{days}</div>
  }

  return (
    <div className='calendar-page'>
        <div className='card'>
          <div className='card-body p-5'>
          {renderHeader()}
          {renderToggles()}
          {viewMode === 'month' && (
            <>
              {renderDaysOfWeek()}
              {renderMonthView()}
            </>
          )}
          {viewMode === 'week' && renderWeekView()}
        </div>
      </div>
      {selectedDayBirthdays && (
        <BirthdayModal
          birthdays={selectedDayBirthdays.birthdays}
          date={selectedDayBirthdays.date}
          onClose={() => setSelectedDayBirthdays(null)}
          onOpenPreview={openPreview}
        />
      )}
      {selectedAppointment && (
        <AppointmentModal
          title={selectedAppointment.tittle}
          date={selectedAppointment.date}
          meetingLocation={selectedAppointment.meetingLocation}
          typeAppointment={selectedAppointment.typeAppointment}
          comment={selectedAppointment.comment}
          leadName={selectedAppointment.lead?.name}
          pipelineName={selectedAppointment.lead?.stagePipeline?.pipeline?.name}
          stageName={selectedAppointment.lead?.stagePipeline?.name}
          onClose={() => setSelectedAppointment(null)}
        />
      )}
      {previewContactId && (
        <ModalPreview idContact={previewContactId} onClose={closePreview} />
      )}
    </div>
  )
}
